import { useCallback, useEffect, useRef, useState } from 'react';
import { AntChatSavedReply, fetchSavedReplies } from './api';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * The saved replies the composer offers on a session in `systemId` (that
 * system's and the global ones). Read on first use (`ensure`), not on
 * every conversation the agent opens, and again after the Manage dialog
 * changes one (`reload`).
 */
export function useSavedReplies(systemId: string | null) {
  const [replies, setReplies] = useState<AntChatSavedReply[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Only the newest read answers: a slow one for another system is dropped. */
  const requestRef = useRef(0);
  const requestedRef = useRef(false);

  useEffect(() => {
    requestRef.current += 1;
    requestedRef.current = false;
    setReplies(null);
    setError(null);
    setLoading(false);
  }, [systemId]);

  const reload = useCallback(async () => {
    requestedRef.current = true;
    requestRef.current += 1;
    const request = requestRef.current;
    setLoading(true);
    try {
      const found = await fetchSavedReplies(systemId);
      if (request !== requestRef.current) return;
      setReplies(found);
      setError(null);
    } catch (e) {
      if (request !== requestRef.current) return;
      setError(errorText(e));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [systemId]);

  const ensure = useCallback(() => {
    // A failed first read still counts as requested: only reload retries it.
    if (!requestedRef.current) reload();
  }, [reload]);

  return { replies, loading, error, ensure, reload };
}
